import { useCallback, useEffect, useState } from "react";
import { partnerApi } from "@/lib/partner";
import { PartnerShell, PartnerLoading, PartnerEmpty, PartnerError } from "@/components/partner/PartnerShell";

export default function PartnerCustomers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    partnerApi
      .customers({ limit: 100, offset: 0 })
      .then((d) => setItems(Array.isArray(d?.items) ? d.items : []))
      .catch((e) => setErr(e?.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PartnerShell testId="partner-customers-page">
      <h1 className="text-3xl font-light tracking-tight">Attributed customers</h1>
      <p className="mt-2 text-sm text-white/50">
        Workspaces attributed to you. Personal details are never shown.
      </p>

      {loading ? (
        <PartnerLoading testId="partner-customers-loading" />
      ) : err ? (
        <PartnerError
          testId="partner-customers-error"
          message={err === "not_a_partner" ? "You’re not a partner yet." : "We couldn’t load your customers."}
          onRetry={err === "not_a_partner" ? undefined : load}
        />
      ) : items.length === 0 ? (
        <PartnerEmpty
          testId="partner-customers-empty"
          title="No referred customers yet"
          sub="Share your referral link to invite them — commissions appear here as their invoices are paid."
        />
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0B0D12]">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-4 py-3">Workspace</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Seats</th>
                <th className="px-4 py-3">Interval</th>
                <th className="px-4 py-3">Currency</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Attributed</th>
              </tr>
            </thead>
            <tbody className="text-white/80">
              {items.map((r) => (
                <tr key={r?.workspace_id} className="border-t border-white/5" data-testid={`cust-${r?.workspace_id}`}>
                  <td className="px-4 py-3">{r?.workspace_name || <span className="text-white/40">—</span>}</td>
                  <td className="px-4 py-3 capitalize">{r?.plan || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{r?.seats ?? "—"}</td>
                  <td className="px-4 py-3">{r?.interval || "—"}</td>
                  <td className="px-4 py-3">{r?.currency || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] ${r?.active ? "border-emerald-500/40 text-emerald-300" : "border-white/15 text-white/50"}`}>
                      {r?.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-white/50">{r?.attributed_at ? new Date(r.attributed_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerShell>
  );
}
